import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingBag,
  CheckSquare,
  Apple,
  Wheat,
  Beef,
  GlassWater,
  Zap,
  Pill,
  Flame,
  Store,
  MapPin,
  Package,
} from 'lucide-react';
import RaceCountdown from '../RaceCountdown';
import type { Race, ShopSection, Shopping } from './types';

const sectionIcons: Record<ShopSection['icon'], React.ReactNode> = {
  protein: <Beef size={14} />,
  carbs: <Wheat size={14} />,
  fruit: <Apple size={14} />,
  condiments: <Flame size={14} />,
  drinks: <GlassWater size={14} />,
  race: <Zap size={14} />,
  supplements: <Pill size={14} />,
  store: <ShoppingBag size={14} />,
  gear: <Package size={14} />,
};

const ShoppingView: React.FC<{ race: Race; shopping: Shopping; label: string; gunLabel: string; storageKey: string }> = ({
  race,
  shopping,
  label,
  gunLabel,
  storageKey,
}) => {
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(checked));
    } catch {
      // Storage unavailable — ticks just won't persist.
    }
  }, [storageKey, checked]);

  const toggle = (id: string) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <>
      <motion.section
        className="fuel-hero ev-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="fuel-hero-inner">
          <motion.div
            className="fuel-hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="fuel-hero-dot" />
            <span>Race-Week Shop</span>
            <span className="fuel-hero-sep">·</span>
            <span>{label}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8 }}
          >
            Groceries <span className="fuel-amp">&amp;</span> Supplements
          </motion.h1>
          <RaceCountdown raceDate={race.date} timezone={race.timezone} detail={gunLabel} />
        </div>
      </motion.section>

      {shopping.stores && shopping.stores.length > 0 && (
        <section className="ev-section">
          <h2 className="section-title ev-section-title">Where to shop</h2>
          <div className="store-grid">
            {shopping.stores.map((s) => (
              <div key={s.name} className="store-card">
                <div className="store-card-head">
                  <Store size={16} />
                  <h3>{s.name}</h3>
                  <span className="store-walk">{s.walk}</span>
                </div>
                <p className="store-address">
                  <MapPin size={11} /> {s.address}
                </p>
                <p className="store-hours">{s.hours}</p>
                <p className="store-best">{s.bestFor}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="ev-section">
        <div className="ev-lede">
          <p className="ev-sub">{shopping.lede} Check items as you buy — progress saves on this device.</p>
        </div>

        <div className="fuel-shop-grid">
          {shopping.sections.map((section) => {
            const sectionChecked = section.items.filter((i) => checked[i.id]).length;
            const sectionDone = sectionChecked === section.items.length;
            return (
              <div
                key={section.title}
                className={`fuel-shop-sec ${section.wide ? 'fuel-shop-wide' : ''} ${sectionDone ? 'fuel-shop-sec-done' : ''}`}
              >
                <div className="fuel-shop-sec-head">
                  <div className={`fuel-shop-cat ${section.accent ? 'fuel-shop-cat-accent' : ''}`}>
                    {sectionIcons[section.icon]}
                    <span>{section.title}</span>
                  </div>
                  <span className="fuel-shop-sec-count">
                    {sectionChecked}/{section.items.length}
                  </span>
                </div>
                <ul className={section.wide ? 'fuel-shop-list fuel-shop-list-two' : 'fuel-shop-list'}>
                  {section.items.map((it) => {
                    const isChecked = !!checked[it.id];
                    return (
                      <li key={it.id} className={isChecked ? 'fuel-shop-li-checked' : ''}>
                        <button
                          className={`fuel-shop-chk ${isChecked ? 'fuel-shop-chk-on' : ''}`}
                          onClick={() => toggle(it.id)}
                          aria-label={`Mark ${it.item} as ${isChecked ? 'not bought' : 'bought'}`}
                        >
                          {isChecked && <CheckSquare size={11} strokeWidth={3} />}
                        </button>
                        <div className="fuel-shop-item">
                          <span className="fuel-shop-item-name">{it.item}</span>
                          {it.detail && <span className="fuel-shop-item-detail">{it.detail}</span>}
                        </div>
                        <span className="fuel-shop-qty">{it.qty}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="fuel-shop-footnote">
          <CheckSquare size={14} />
          <p>{shopping.footnote}</p>
        </div>
      </section>
    </>
  );
};

export default ShoppingView;
